import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, ClipboardList, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/j7ntu5ag_HOME.png";

const features = [
  {
    icon: BookOpen,
    titleKey: "feature_exams_title",
    descKey: "feature_exams_desc"
  },
  {
    icon: ClipboardList,
    titleKey: "feature_anamnesis_title",
    descKey: "feature_anamnesis_desc"
  },
  {
    icon: Users,
    titleKey: "feature_clients_title",
    descKey: "feature_clients_desc"
  },
  {
    icon: FileCheck,
    titleKey: "feature_mobile_title",
    descKey: "feature_mobile_desc"
  }
];

export const Landing = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src={LOGO_URL} alt="AESTHA" className="h-12 invert" />
          <div className="flex items-center gap-4">
            <LanguageSelector variant="nav" />
            <Link to="/login" className="nav-link hidden sm:block" data-testid="login-link">
              {t("login")}
            </Link>
            <Link to="/register">
              <Button className="btn-primary h-10 px-6" data-testid="register-button">
                {t("register")}
              </Button>
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 px-6" data-testid="hero-section">
        {/* Mobile Hero Image - Visible only on mobile */}
        <div className="lg:hidden mb-8 animate-fadeIn">
          <div className="relative aspect-[3/4] max-h-[50vh] mx-auto overflow-hidden bg-secondary">
            <img 
              src={HERO_IMAGE} 
              alt="Estética" 
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/80 to-transparent h-24" />
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="animate-slideUp text-center lg:text-left">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
              {t("platform_for")}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-tight mb-6">
              {t("hero_title")} {t("hero_title_end")}
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground mb-8 max-w-md mx-auto lg:mx-0">
              {t("hero_description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link to="/register">
                <Button className="btn-primary w-full sm:w-auto" data-testid="hero-cta">
                  {t("create_account_free")}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="btn-secondary w-full sm:w-auto">
                  {t("already_have_account")}
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Desktop Hero Image */}
          <div className="relative animate-fadeIn hidden lg:block">
            <div className="aspect-[4/5] bg-secondary overflow-hidden">
              <img 
                src={HERO_IMAGE} 
                alt="Estética" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-48 h-48 border bg-white p-6 flex flex-col justify-center">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {t("active_members")}
              </p>
              <p className="font-serif text-4xl">500+</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="divider-asterisk max-w-7xl mx-auto px-6">
        <span className="font-serif text-2xl">✦</span>
      </div>
      
      {/* Features */}
      <section className="py-12 lg:py-20 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 lg:mb-16">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
              {t("features")}
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl">
              {t("everything_you_need")}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.titleKey}
                  className={`bento-item p-4 lg:p-6 animate-slideUp stagger-${index + 1}`}
                  data-testid={`feature-card-${index}`}
                >
                  <Icon size={28} strokeWidth={1} className="mb-3 lg:mb-4" />
                  <h3 className="font-serif text-base lg:text-xl mb-1 lg:mb-2">{t(feature.titleKey)}</h3>
                  <p className="text-xs lg:text-sm text-muted-foreground">{t(feature.descKey)}</p>
                </div>
              );
            })}
          </div>
          
          {/* Mobile compatibility text */}
          <p className="text-center text-sm text-muted-foreground mt-8">
            {t("mobile_compatibility")}
          </p>
        </div>
      </section>
      
      {/* Emotional Section */}
      <section className="py-12 lg:py-20 px-6 bg-secondary/30" data-testid="emotional-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-4 lg:mb-6 leading-tight">
            {t("emotional_title")}
          </h2>
          <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("emotional_description")}
          </p>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-12 lg:py-20 px-6 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl mb-4 lg:mb-6">
            {t("ready_to_start")}
          </h2>
          <p className="text-base lg:text-lg opacity-80 mb-6 lg:mb-8">
            {t("join_hundreds")}
          </p>
          <Link to="/register">
            <Button className="bg-white text-black hover:bg-white/90 h-11 lg:h-12 px-6 lg:px-8 uppercase tracking-widest text-xs font-bold font-mono">
              {t("create_my_account")}
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 px-6 border-t" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="AESTHA" className="h-12 invert" />
          <p className="text-sm text-muted-foreground font-mono">
            © {new Date().getFullYear()} AESTHA by Isabella Barros
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
